import { useLayoutEffect, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useProjectLoading } from 'contexts/ProjectLoadingContext';
import { PROJECT_ROUTE_PATTERN } from 'routes/routes.constants';
import styled from 'styled-components';

import { usePathname } from '@polyai/common/hooks/useRouter';
import { SidebarProvider } from '@polyai/ui/components/organisms/Sidebar';

import ContentLoaderArea from 'components/atoms/ContentLoaderArea';
import HeaderSkeleton from 'components/molecules/Header/HeaderSkeleton';
import ProjectSidebarSkeleton from 'components/organisms/ProjectSidebar/ProjectSidebarSkeleton';

const Overlay = styled.div<{ $hidden: boolean }>`
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};

  position: fixed;
  z-index: 9999;
  inset: 0;

  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};

  transition: opacity 0.15s ease-out, visibility 0.15s ease-out;
`;

/**
 * A persistent loading overlay that stays mounted to prevent
 * skeleton flicker during auth → project loading transitions.
 *
 * Shows nav/header skeleton until basic project data loads,
 * then shows only content skeleton until full projection loads.
 */
const ProjectLoadingOverlay = () => {
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth0();
  const { isProjectLoading } = useProjectLoading();
  const pathname = usePathname();

  // Track previous pathname to detect route transitions
  const prevPathnameRef = useRef<string | null>(null);

  // Check if we're on a project route (matches /:accountId/PROJECT-xxx/...)
  const isProjectRoute = PROJECT_ROUTE_PATTERN.test(pathname);
  const wasProjectRoute =
    prevPathnameRef.current !== null &&
    PROJECT_ROUTE_PATTERN.test(prevPathnameRef.current);

  // Update ref after render but before paint
  useLayoutEffect(() => {
    prevPathnameRef.current = pathname;
  });

  // Detect entering a project route from elsewhere (including initial load on a project route)
  // On this first render, the context may have stale state, so force loading to be true
  const justEnteredProjectRoute = isProjectRoute && !wasProjectRoute;

  // Force loading state when entering a project route to prevent flash from stale context
  const effectiveIsProjectLoading = justEnteredProjectRoute || isProjectLoading;

  // Hide overlay when auth is complete AND all loading is done
  const isHidden =
    !isAuthLoading && isAuthenticated && !effectiveIsProjectLoading;

  if (!isProjectRoute) {
    return null;
  }

  // Always show both skeletons whenever the overlay is visible to prevent loader position shift.
  // This ensures the nav/header skeleton and content loader appear together from the start.
  const showSkeletons =
    isAuthLoading || !isAuthenticated || effectiveIsProjectLoading;

  return (
    <Overlay $hidden={isHidden}>
      {showSkeletons && (
        <SidebarProvider>
          <ProjectSidebarSkeleton />
          <div className="flex flex-1 flex-col">
            <HeaderSkeleton />
            <ContentLoaderArea useMediaQueryForOffset />
          </div>
        </SidebarProvider>
      )}
    </Overlay>
  );
};

export default ProjectLoadingOverlay;
